import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import axios from 'axios';
import { API_URL } from '../../config/api';
import i18n from '../../i18n';
import './AdminLayout.css';

const AdminLayout = ({ children }) => {
  const { t, i18n: i18nInstance } = useTranslation();
  const { user, logout, loading, updateUser } = useAuth();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > 768;
    }
    return true;
  });
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLang = localStorage.getItem('i18nextLng') || i18nInstance.language || 'en';
    return savedLang.startsWith('fi') ? 'fi' : 'en';
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });
  const locationIntervalRef = useRef(null);

  const handleThemeChange = async (value) => {
    if (value !== 'light' && value !== 'dark' && value !== 'device') return;
    setPreference(value);
    if (!user) return;
    try {
      const newSettings = { ...(user.settings || {}), themePreference: value };
      const response = await axios.put(`${API_URL}/users/settings`, newSettings);
      if (response.data?.settings) {
        updateUser({ ...user, settings: response.data.settings });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Lock body scroll when sidebar is open on mobile so menu closes fully
  useEffect(() => {
    if (sidebarOpen && isMobile) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [sidebarOpen, isMobile]);

  // Redirect to login if user is not authenticated
  useEffect(() => {
    if (!loading && !user) {
      navigate('/admin/login', { replace: true });
    }
  }, [user, loading, navigate]);

  // Location tracking for delivery drivers - works on all admin pages
  useEffect(() => {
    // Only for delivery drivers
    if (!user || user.role !== 'delivery_driver') {
      return;
    }

    const userId = user._id || user.id;
    if (!userId) {
      return;
    }

    const updateLocation = async () => {
      if (!navigator.geolocation) {
        console.warn('Geolocation is not supported by your browser');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            
            const response = await axios.patch(
              `${API_URL}/delivery-driver/location`,
              { lat, lng },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            );
            
            if (response.data.success && response.data.currentLocation && updateUser) {
              updateUser({ 
                ...user, 
                currentLocation: response.data.currentLocation 
              });
            }
          } catch (error) {
            console.error('Error updating location:', error);
            // Don't disable tracking on error - just log it and continue
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Don't disable tracking on geolocation error - user might have temporarily denied permission
          // Tracking will continue to try on next interval
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    };

    const checkAndStartTracking = () => {
      // Check localStorage for current state
      const savedLocationEnabled = localStorage.getItem(`locationTracking_${userId}`);
      const isLocationEnabled = savedLocationEnabled === 'true';

      // If tracking should be enabled but interval doesn't exist, start it
      if (isLocationEnabled && !locationIntervalRef.current) {
        // Initial location update
        updateLocation();
        // Update location to server every 10 seconds
        // Note: Map view in DeliveryView refreshes every 30 seconds to show updated locations
        locationIntervalRef.current = setInterval(() => {
          updateLocation();
        }, 10000); // 10 seconds for location updates to server
      }
      // If tracking should be disabled but interval exists, stop it
      else if (!isLocationEnabled && locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
    };

    // Check and start tracking on mount
    checkAndStartTracking();

    // Listen for storage changes (when Settings page updates localStorage in another tab)
    const handleStorageChange = (e) => {
      if (e.key === `locationTracking_${userId}`) {
        checkAndStartTracking();
      }
    };

    // Listen for custom event (for same-tab updates)
    const handleLocationTrackingChange = (e) => {
      // Check if the event is for this user
      const eventUserId = e.detail?.userId;
      if (!eventUserId || eventUserId === userId) {
        checkAndStartTracking();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('locationTrackingChanged', handleLocationTrackingChange);

    // Cleanup on unmount or when user changes
    return () => {
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
        locationIntervalRef.current = null;
      }
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('locationTrackingChanged', handleLocationTrackingChange);
    };
  }, [user, updateUser]);

  const handleLogout = () => {
    logout();
    // Small delay to ensure state is cleared before navigation
    setTimeout(() => {
      navigate('/admin/login', { replace: true });
    }, 100);
  };

  useEffect(() => {
    // Initialize current language from i18n
    const savedLang = localStorage.getItem('i18nextLng') || i18nInstance.language || 'en';
    const lang = savedLang.startsWith('fi') ? 'fi' : 'en';
    setCurrentLanguage(lang);
    if (i18nInstance.language !== lang) {
      i18nInstance.changeLanguage(lang);
    }

    const handleLanguageChange = (lng) => {
      const normalizedLang = lng.startsWith('fi') ? 'fi' : 'en';
      setCurrentLanguage(normalizedLang);
    };
    i18nInstance.on('languageChanged', handleLanguageChange);
    return () => {
      i18nInstance.off('languageChanged', handleLanguageChange);
    };
  }, [i18nInstance]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLanguageMenu && !event.target.closest('.admin-language-menu')) {
        setShowLanguageMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageMenu]);

  const handleLanguageChange = async (lang) => {
    i18nInstance.changeLanguage(lang);
    setCurrentLanguage(lang);
    setShowLanguageMenu(false);
    
    // Update user settings if user is logged in
    if (user) {
      try {
        await axios.put(`${API_URL}/users/settings`, {
          ...user.settings,
          language: lang
        });
        if (updateUser) {
          updateUser({ ...user, settings: { ...user.settings, language: lang } });
        }
      } catch (error) {
        console.error('Error updating language setting:', error);
      }
    }
  };

  const getNavItems = () => {
    const mainAdminNav = [
      { path: '/admin/main/dashboard', label: t('admin.dashboard'), icon: '📊' },
          { path: '/admin/main/users', label: t('admin.users'), icon: '👥' },
          { path: '/admin/main/restaurants', label: t('admin.restaurants'), icon: '🍽️' },
          { path: '/admin/main/stores', label: t('admin.stores'), icon: '🏪' },
          { path: '/admin/main/variation-templates', label: t('admin.variationTemplates'), icon: '📋' },
          { path: '/admin/main/orders', label: t('admin.orders'), icon: '📦' },
          { path: '/admin/main/messages', label: t('admin.messages'), icon: '💬' },
          { path: '/admin/main/discount-codes', label: t('admin.discountCodes'), icon: '🎫' },
          { path: '/admin/main/contact-submissions', label: t('admin.contactSubmissions'), icon: '📩' },
          { path: '/admin/main/business-intelligence', label: t('admin.businessIntelligence'), icon: '📈' },
          { path: '/admin/main/reviews', label: t('admin.reviews'), icon: '⭐' },
          { path: '/admin/main/delivery', label: t('admin.delivery'), icon: '🚚' },
          { path: '/admin/main/invoicing', label: t('admin.invoicing'), icon: '💰' },
          { path: '/admin/main/media-gallery', label: t('admin.mediaGallery'), icon: '🖼️' },
          { path: '/admin/main/landing-content', label: t('admin.landingContent'), icon: '📰' },
          { path: '/admin/driver/delivery-view', label: t('admin.deliveryView'), icon: '🗺️' },
        ];
    switch (user?.role) {
      case 'main_admin':
        return [
          ...mainAdminNav,
          { path: '/admin/main/website-settings', label: t('admin.websiteSettings'), icon: '⚙️' },
        ];
      case 'admin':
        return mainAdminNav;
      case 'invoicer':
        return [
          { path: '/admin/main/invoicing', label: t('admin.invoicing'), icon: '💰' },
          { path: '/admin/main/business-intelligence', label: t('admin.businessIntelligence'), icon: '📈' },
        ];
      case 'restaurant_owner':
        return [
          { path: '/admin/restaurant/dashboard', label: t('admin.dashboard'), icon: '📊' },
          { path: '/admin/restaurant/management', label: t('admin.restaurants'), icon: '🍽️' },
          { path: '/admin/restaurant/orders', label: t('admin.orders'), icon: '📦' },
          { path: '/admin/restaurant/messages', label: t('admin.messages'), icon: '💬' },
          { path: '/admin/restaurant/reviews', label: t('admin.reviews'), icon: '⭐' },
          { path: '/admin/restaurant/business-intelligence', label: t('admin.businessIntelligence'), icon: '📈' },
          { path: '/admin/restaurant/media-gallery', label: t('admin.mediaGallery'), icon: '🖼️' },
        ];
      case 'restaurant_user':
        return [
          { path: '/admin/restaurant/dashboard', label: t('admin.dashboard'), icon: '📊' },
          { path: '/admin/restaurant/management', label: t('admin.restaurants'), icon: '🍽️' },
          { path: '/admin/restaurant/orders', label: t('admin.orders'), icon: '📦' },
          { path: '/admin/restaurant/messages', label: t('admin.messages'), icon: '💬' },
          { path: '/admin/restaurant/business-intelligence', label: t('admin.businessIntelligence'), icon: '📈' },
          { path: '/admin/restaurant/media-gallery', label: t('admin.mediaGallery'), icon: '🖼️' },
        ];
      case 'store_owner':
      case 'store_user':
        return [
          { path: '/admin/store/dashboard', label: t('admin.dashboard'), icon: '📊' },
          { path: '/admin/store/management', label: t('admin.stores'), icon: '🏪' },
          { path: '/admin/store/orders', label: t('admin.orders'), icon: '📦' },
          { path: '/admin/store/messages', label: t('admin.messages'), icon: '💬' },
          { path: '/admin/store/reviews', label: t('admin.reviews'), icon: '⭐' },
          { path: '/admin/store/business-intelligence', label: t('admin.businessIntelligence'), icon: '📈' },
          { path: '/admin/store/media-gallery', label: t('admin.mediaGallery'), icon: '🖼️' },
        ];
      case 'delivery_driver':
        return [
          { path: '/admin/driver/dashboard', label: t('admin.dashboard'), icon: '📊' },
          { path: '/admin/driver/orders', label: t('admin.orders'), icon: '📦' },
          { path: '/admin/driver/messages', label: t('admin.messages'), icon: '💬' },
          { path: '/admin/driver/delivery-view', label: t('admin.deliveryView'), icon: '🗺️' },
          { path: '/admin/driver/business-intelligence', label: t('admin.businessIntelligence'), icon: '📈' },
          { path: '/admin/driver/settings', label: t('admin.settings'), icon: '⚙️' },
        ];
      default:
        return [];
    }
  };

  const getRoleLabel = () => {
    const labels = {
      main_admin: t('admin.mainAdmin'),
      admin: t('admin.roleAdmin'),
      invoicer: t('admin.invoicer'),
      restaurant_owner: t('admin.restaurantOwner'),
      restaurant_user: t('admin.restaurantUser'),
      store_owner: t('admin.storeOwner'),
      store_user: t('admin.storeUser'),
      delivery_driver: t('admin.deliveryDriver'),
    };
    return labels[user?.role] || t('admin.title');
  };

  return (
    <div className="admin-layout">
      {sidebarOpen && isMobile && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>
      )}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="logo-container">
            <img src="/images/logo.webp" alt="Pussikuskit Logo" className="sidebar-logo" />
            <h2 className="admin-title">{t('admin.title')}</h2>
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="toggle-btn">
            {sidebarOpen ? '◀' : '▶'}
          </button>
        </div>
        <nav className="sidebar-nav">
          {getNavItems().map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => isMobile && setSidebarOpen(false)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            {sidebarOpen && (
              <>
                <div className="user-name">{user?.firstName} {user?.lastName}</div>
                <div className="user-role">{getRoleLabel()}</div>
              </>
            )}
          </div>
          <div className="admin-theme-row" style={{ marginBottom: '10px' }}>
            {sidebarOpen ? (
              <>
                <label className="admin-theme-label">{t('settings.theme') || 'Theme'}</label>
                <select
                  value={preference}
                  onChange={(e) => handleThemeChange(e.target.value)}
                  className="admin-theme-select"
                  aria-label={t('settings.theme') || 'Theme'}
                >
                  <option value="device">{t('settings.themeFollowDevice') || 'Follow device'}</option>
                  <option value="light">{t('settings.themeLight') || 'Light'}</option>
                  <option value="dark">{t('settings.themeDark') || 'Dark'}</option>
                </select>
              </>
            ) : (
              <button
                type="button"
                className="admin-theme-icon-btn"
                onClick={() => handleThemeChange(preference === 'dark' ? 'light' : preference === 'light' ? 'device' : 'dark')}
                title={t('settings.theme') || 'Theme'}
                aria-label={t('settings.theme') || 'Theme'}
              >
                {preference === 'dark' ? '🌙' : preference === 'light' ? '☀️' : '💻'}
              </button>
            )}
          </div>
          <div className="admin-language-menu" style={{ position: 'relative', marginBottom: '10px' }}>
            {sidebarOpen ? (
              <div className="language-toggle-buttons">
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLanguageChange('en');
                  }}
                  className={`language-toggle-btn ${currentLanguage === 'en' ? 'active' : ''}`}
                  type="button"
                >
                  EN
                </button>
                <span className="language-separator">|</span>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLanguageChange('fi');
                  }}
                  className={`language-toggle-btn ${currentLanguage === 'fi' ? 'active' : ''}`}
                  type="button"
                >
                  FI
                </button>
              </div>
            ) : (
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowLanguageMenu(!showLanguageMenu);
                }} 
                className="language-btn"
                type="button"
              >
                🌐
              </button>
            )}
            {!sidebarOpen && showLanguageMenu && (
              <div className="language-dropdown" onClick={(e) => e.stopPropagation()}>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLanguageChange('en');
                  }}
                  className={`language-option ${currentLanguage === 'en' ? 'active' : ''}`}
                  type="button"
                >
                  EN
                </button>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLanguageChange('fi');
                  }}
                  className={`language-option ${currentLanguage === 'fi' ? 'active' : ''}`}
                  type="button"
                >
                  FI
                </button>
              </div>
            )}
          </div>
          <button onClick={() => navigate('/')} className="home-btn">
            {sidebarOpen ? t('admin.home') : '🏠'}
          </button>
          <button onClick={handleLogout} className="logout-btn">
            {sidebarOpen ? t('admin.logout') : '🚪'}
          </button>
        </div>
      </aside>
      <main className="admin-main">
        <header className="admin-header">
          <button className="mobile-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            ☰
          </button>
          <img src="/images/logo.webp" alt="Pussikuskit" className="admin-header-logo" />
          <h1>{t('admin.adminDashboard')}</h1>
        </header>
        <div className="admin-content">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
